import React, { ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { AppVideoPlayer } from "../../components";
import { IMAGE_ACCEPT } from "../../constants";
import { MediaTypeEnum } from "../../enums";
import { MediaDto, MediaStatusEnum } from "../../interfaces/media";
import {
  useDeleteLiveLectureMediaMutation,
  useUploadLiveLectureImageMutation,
} from "../../store/api/liveLectureApi";

import imagePlaceholder from "../../assets/images/placeholder/image-placeholder.png";
import { MediaStatus } from "../../components/Common";

type TProps = {
  liveLectureId?: string | null;
  media?: MediaDto[];
  pendingMedia: MediaDto[];
  onPendingMediaChange: (media: MediaDto[]) => void;
  disabled?: boolean;
};

export const LiveLectureGallery: React.FC<TProps> = ({
  liveLectureId,
  media = [],
  pendingMedia,
  onPendingMediaChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string>();
  const [showPlayerModal, setShowPlayerModal] = useState(false);

  const [uploadImage, { isLoading: isUploadingImage }] =
    useUploadLiveLectureImageMutation();
  const [deleteMedia, { isLoading: isDeletingMedia }] =
    useDeleteLiveLectureMediaMutation();

  const galleryItems = useMemo(() => {
    const items = liveLectureId ? media : pendingMedia;
    return items.filter((item) => item.type !== MediaTypeEnum.DOCUMENT);
  }, [liveLectureId, media, pendingMedia]);

  const isUploading = isUploadingImage || isDeletingMedia;

  const getPreviewSrc = (item: MediaDto) => {
    if (item.type === MediaTypeEnum.VIDEO || item.videoUrl || item.thumbnail) {
      return item.thumbnail || imagePlaceholder;
    }
    return item.path || imagePlaceholder;
  };

  const getPlayableUrl = (item: MediaDto) => {
    if (item.type === MediaTypeEnum.VIDEO || item.videoUrl) {
      if (item.status === MediaStatusEnum.COMPLETED && item.videoUrl) {
        return item.videoUrl;
      }
      return null;
    }
    return item.path;
  };

  const onUploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      setUploadProgress(0);
      const uploaded = await uploadImage({
        file,
        liveLectureId,
        onProgress: setUploadProgress,
      }).unwrap();

      if (!liveLectureId) {
        onPendingMediaChange([...pendingMedia, uploaded]);
      }
    } finally {
      setUploadProgress(null);
    }
  };

  const onRemove = async (item: MediaDto) => {
    if (liveLectureId) {
      await deleteMedia({
        liveLectureId,
        mediaId: item.id,
      }).unwrap();
      return;
    }

    onPendingMediaChange(
      pendingMedia.filter((mediaItem) => mediaItem.id !== item.id),
    );
  };

  const onPlay = (item: MediaDto) => {
    const url = getPlayableUrl(item);
    if (!url) return;
    setPlayingVideoUrl(url);
    setShowPlayerModal(true);
  };

  return (
    <React.Fragment>
      <div className="mb-3">
        <Label className="form-label">{t("liveLectureGallery")}</Label>
        <p className="text-muted small mb-3">{t("liveLectureGalleryHint")}</p>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Label
            className={`btn btn-soft-primary mb-0 ${disabled || isUploading ? "disabled" : ""}`}
            htmlFor="live-lecture-gallery-image"
          >
            <i className="ri-image-add-line me-1"></i>
            {t("uploadImage")}
          </Label>
          <input
            disabled={disabled || isUploading}
            type="file"
            className="d-none"
            id="live-lecture-gallery-image"
            accept={IMAGE_ACCEPT}
            onChange={onUploadImage}
          />

          {uploadProgress !== null && (
            <span className="d-inline-flex align-items-center text-muted small">
              <Spinner size="sm" className="me-2" />
              {t("uploading")} {uploadProgress}%
            </span>
          )}
        </div>

        {galleryItems.length === 0 ? (
          <div className="text-muted small">{t("liveLectureGalleryEmpty")}</div>
        ) : (
          <Row className="g-3">
            {galleryItems.map((item) => {
              const isVideo =
                item.type === MediaTypeEnum.VIDEO || Boolean(item.videoUrl);

              return (
                <Col key={item.id} xs={6} md={4} lg={3}>
                  <div className="border rounded overflow-hidden h-100">
                    <div className="position-relative">
                      <img
                        src={getPreviewSrc(item)}
                        alt={item.name}
                        className="w-100 object-fit-cover"
                        style={{ height: 140 }}
                      />
                      {isVideo && (
                        <Button
                          color="dark"
                          size="sm"
                          className="position-absolute top-50 start-50 translate-middle rounded-circle"
                          onClick={() => onPlay(item)}
                          disabled={!getPlayableUrl(item)}
                        >
                          <i className="ri-play-fill"></i>
                        </Button>
                      )}
                    </div>
                    <div className="p-2">
                      <div
                        className="text-truncate small fw-medium"
                        title={item.name}
                      >
                        {item.name}
                      </div>
                      <div className="d-flex align-items-center justify-content-between mt-2 gap-2">
                        <Button
                          color="link"
                          className="text-danger p-0"
                          onClick={() => onRemove(item)}
                          disabled={disabled || isUploading}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                        {item.type === MediaTypeEnum.VIDEO &&
                        item.status !== MediaStatusEnum.COMPLETED ? (
                          <MediaStatus
                            type={"LiveLecture"}
                            lectureOrSkillId={liveLectureId as string}
                            status={item.status || MediaStatusEnum.NEW}
                            mediaId={item.id}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      <Modal
        isOpen={showPlayerModal}
        toggle={() => setShowPlayerModal(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setShowPlayerModal(false)}>
          {t("previewVideo")}
        </ModalHeader>
        <ModalBody>
          {playingVideoUrl && <AppVideoPlayer src={playingVideoUrl} />}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};
